import React from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import Meta from "../Meta";
import Loader from "../loader";
import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import { applyLang } from "../../../i18n";
import renderIcon from "../../../components/renderIcon";
import { ASSET_URL } from "../../../config";
import Link from "next/link";
import StarRating from "../components/StarRating";
import { walkMinutesFrom, getBethHabadLabel } from "../utils";

const attractionStyles = `
.tr-attractions__title {
  font-size: clamp(24px, 4vw, 40px);
  font-weight: 800;
  color: #1a1a2e;
  text-align: center;
  margin: 56px 0 32px;
  padding: 0 16px;
}

/* ── Filtres catégories ── */
.tr-attractions__cats {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 12px;
  margin-bottom: 48px;
  padding: 0 16px;
}
.tr-attractions__cat {
  padding: 10px 22px;
  border-radius: 999px;
  border: none;
  background: color-mix(in srgb, var(--primary, #1b579e) 12%, white);
  color: var(--primary, #1b579e);
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: background .15s, color .15s, transform .15s;
}
.tr-attractions__cat:hover {
  background: color-mix(in srgb, var(--primary, #1b579e) 20%, white);
}
.tr-attractions__cat--active {
  background: var(--primary, #1b579e);
  color: white;
}

/* ── Section par catégorie ── */
.tr-attractions__section {
  margin-bottom: 56px;
}
.tr-attractions__section-title {
  font-size: clamp(20px, 2.5vw, 28px);
  font-weight: 700;
  color: #1a1a2e;
  text-align: center;
  margin: 0 0 28px;
}

/* ── Grille ── */
.tr-attractions__grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 360px));
  gap: 24px;
  justify-content: center;
  max-width: 1180px;
  margin: 0 auto;
}
@media (max-width: 640px) {
  .tr-attractions__grid { grid-template-columns: minmax(0, 400px); }
}

/* ── Carte ── */
.tr-acard {
  position: relative;
  aspect-ratio: 3/4;
  border-radius: 20px;
  overflow: hidden;
  cursor: pointer;
  box-shadow: 0 4px 16px rgba(0,0,0,.08), 0 1px 4px rgba(0,0,0,.04);
  transition: transform .25s ease, box-shadow .25s ease;
  background: #1a1a2e;
  text-decoration: none;
  color: inherit;
  display: block;
}
.tr-acard:hover {
  transform: translateY(-4px);
  box-shadow: 0 14px 32px rgba(0,0,0,.16), 0 2px 6px rgba(0,0,0,.06);
}
.tr-acard__img {
  position: absolute; inset: 0;
  width: 100%; height: 100%;
  object-fit: cover;
  transition: transform .4s ease;
}
.tr-acard:hover .tr-acard__img { transform: scale(1.06); }
.tr-acard__img-placeholder {
  position: absolute; inset: 0;
  display: flex; align-items: center; justify-content: center;
  color: #4b5563;
  font-size: 40px;
  background: linear-gradient(135deg, #374151 0%, #1f2937 100%);
}
.tr-acard__wash {
  position: absolute; inset: 0;
  background: linear-gradient(180deg, rgba(9,18,26,0) 30%, rgba(9,18,26,.55) 60%, rgba(9,18,26,.92) 100%);
}
.tr-acard__body {
  position: absolute; inset: auto 0 0 0;
  padding: 18px 20px 20px;
  color: white;
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.tr-acard__badges {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
}
.tr-acard__badge {
  display: inline-block;
  font-size: 11px;
  font-weight: 700;
  padding: 4px 10px;
  border-radius: 6px;
  letter-spacing: .02em;
  background: var(--primary, #1b579e);
  color: white;
}
.tr-acard__name {
  font-size: 18px;
  font-weight: 700;
  line-height: 1.25;
  color: white;
}
.tr-acard__meta {
  display: flex;
  align-items: center;
  gap: 14px;
  font-size: 13px;
  color: rgba(255,255,255,.88);
  line-height: 1.35;
  padding-top: 8px;
  border-top: 1px solid rgba(255,255,255,.15);
  flex-wrap: wrap;
}
.tr-acard__meta-item {
  display: flex;
  align-items: center;
  gap: 6px;
}
.tr-acard__meta-icon { flex-shrink: 0; margin-top: 1px; }

/* ── Empty state ── */
.tr-attractions__empty {
  text-align: center;
  padding: 64px 16px;
  color: #6b7280;
  font-size: 15px;
}
`;

function AttractionCard({ context, place, category, lang }) {
    const localized = applyLang(place, lang);
    const localizedCat = category ? applyLang(category, lang) : null;
    const imgSrc = place.image ? `${ASSET_URL}${place.image}` : null;
    const address = place.address?.address;
    const walkMinutes = walkMinutesFrom(context.org, place);

    return (
        <Link href={context.org.BASE_WEB_URL + "/travel/attractions/" + place.id} legacyBehavior>
            <a className="tr-acard">
                {imgSrc ? (
                    <img src={imgSrc} alt={localized.name} className="tr-acard__img" loading="lazy" />
                ) : (
                    <div className="tr-acard__img-placeholder">
                        {renderIcon({ name: "IconCamera", source: "tabler" }, 48)}
                    </div>
                )}
                <div className="tr-acard__wash" />
                <div className="tr-acard__body">
                    <div className="tr-acard__badges">
                        {localizedCat?.label && (
                            <span className="tr-acard__badge">{localizedCat.label}</span>
                        )}
                        <StarRating value={place.note} size={15} />
                    </div>
                    <div className="tr-acard__name">{localized.name}</div>
                    {(address || walkMinutes != null) && (
                        <div className="tr-acard__meta">
                            {address && (
                                <span className="tr-acard__meta-item">
                                    <span className="tr-acard__meta-icon">
                                        {renderIcon({ name: "IconMapPin", source: "tabler" }, 14)}
                                    </span>
                                    <span>{address}</span>
                                </span>
                            )}
                            {walkMinutes != null && (
                                <span className="tr-acard__meta-item">
                                    <span className="tr-acard__meta-icon">
                                        {renderIcon({ name: "IconWalk", source: "tabler" }, 14)}
                                    </span>
                                    <span><Trans>{walkMinutes} min</Trans></span>
                                </span>
                            )}
                        </div>
                    )}
                </div>
            </a>
        </Link>
    );
}

export default function Attractions({ context, loading }) {
    const { org, config, travel } = context;

    const categories = (travel?.attraction?.categories || []).map(c => applyLang(c, context.lang));
    const allPlaces = (travel?.attractionPlaces || []).slice().sort((a, b) => (a.position || 0) - (b.position || 0));

    const usedCatIds = new Set(allPlaces.map(p => p.categoryId).filter(Boolean));
    const activeCategories = categories.filter(c => usedCatIds.has(c.id));

    const [activeCat, setActiveCat] = React.useState(null);

    const visiblePlaces = activeCat
        ? allPlaces.filter(p => p.categoryId === activeCat)
        : allPlaces;

    const placesByCategory = React.useMemo(() => {
        const map = new Map();
        for (const cat of activeCategories) {
            map.set(cat.id, visiblePlaces.filter(p => p.categoryId === cat.id));
        }
        const orphans = visiblePlaces.filter(p => !p.categoryId || !activeCategories.find(c => c.id === p.categoryId));
        if (orphans.length > 0) map.set("__other__", orphans);
        return map;
    }, [visiblePlaces, activeCategories]);

    const categoryById = React.useMemo(() => {
        const map = new Map();
        for (const c of categories) map.set(c.id, c);
        return map;
    }, [categories]);

    // JSON-LD ItemList de TouristAttraction.
    const itemListElements = allPlaces.slice(0, 50).map((p, i) => {
        const loc = applyLang(p, context.lang);
        return {
            "@type": "ListItem",
            position: i + 1,
            item: {
                "@type": "TouristAttraction",
                name: loc.name,
                image: p.image ? (p.image.startsWith("http") ? p.image : `${ASSET_URL}${p.image}`) : undefined,
                address: p.address?.address ? {
                    "@type": "PostalAddress",
                    streetAddress: p.address.address,
                } : undefined,
                geo: (p.address?.lat && p.address?.lon) ? {
                    "@type": "GeoCoordinates",
                    latitude: p.address.lat,
                    longitude: p.address.lon,
                } : undefined,
            },
        };
    });
    const jsonLd = itemListElements.length > 0 ? {
        "@context": "https://schema.org",
        "@type": "ItemList",
        name: t`À voir et à faire à ${org.ville || org.name}`,
        itemListElement: itemListElements,
    } : null;

    return (
        <div className="travel-page travel-page--attractions tr-attractions">
            <style dangerouslySetInnerHTML={{ __html: attractionStyles }} />
            <Meta
                context={context}
                title={t`À voir et à faire à ${org.ville || org.name}`}
                description={config["meta.description"] || t`Activités, visites et sites touristiques à découvrir près du ${getBethHabadLabel(travel, context.lang)} de ${org.ville || org.name}`}
                jsonLd={jsonLd}
            />

            {loading && <Loader />}
            <Header context={context} />

            <main className="travel-main">
                <div className="travel-container">

                    <h1 className="tr-attractions__title">
                        <Trans>À voir et à faire à</Trans>{" "}{org.ville || org.name}
                    </h1>

                    {activeCategories.length > 0 && (
                        <div className="tr-attractions__cats">
                            {activeCategories.map(cat => (
                                <button
                                    key={cat.id}
                                    className={`tr-attractions__cat${activeCat === cat.id ? " tr-attractions__cat--active" : ""}`}
                                    onClick={() => setActiveCat(activeCat === cat.id ? null : cat.id)}
                                >
                                    {cat.label}
                                </button>
                            ))}
                        </div>
                    )}

                    {allPlaces.length === 0 && (
                        <div className="tr-attractions__empty">
                            <Trans>Aucune attraction à afficher pour le moment.</Trans>
                        </div>
                    )}

                    {Array.from(placesByCategory.entries()).map(([catId, places]) => {
                        if (places.length === 0) return null;
                        const cat = catId === "__other__" ? null : categoryById.get(catId);
                        const catLocalized = cat ? applyLang(cat, context.lang) : null;
                        return (
                            <section key={catId} className="tr-attractions__section">
                                <h2 className="tr-attractions__section-title">
                                    {catLocalized?.label || <Trans>Autres</Trans>}
                                </h2>
                                <div className="tr-attractions__grid">
                                    {places.map(place => (
                                        <AttractionCard
                                            context={context}
                                            key={place.id}
                                            place={place}
                                            category={cat}
                                            lang={context.lang}
                                        />
                                    ))}
                                </div>
                            </section>
                        );
                    })}

                </div>
            </main>

            <Footer context={context} />
        </div>
    );
}
